<template>
  <div class="shared-modal" :class="{ isMobile }">
    <!-- 顶部标题栏 -->
    <div class="modal-header">
      <div class="header-left" @click="handleBack">
        <svg class="back-icon" viewBox="0 0 1024 1024">
          <path d="M724 96 L316 512 L724 928" fill="none" stroke="currentColor" stroke-width="100" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="modal-title">共享知识</span>
      </div>
    </div>

    <div class="shared-body">
      <!-- ★ 三个重叠卡片：tab 头 + 卡片体 -->
      <div class="tab-cards">
        <!-- tab 标题栏 -->
        <div class="tab-headers">
          <div v-for="lv in levels" :key="lv.key" class="tab-header"
               :class="{ active: activeLevel === lv.key }"
               @click="switchLevel(lv.key)">
            {{ lv.label }}
            <span class="count" v-if="filteredList(lv.key).length">
              {{ filteredList(lv.key).length }}
            </span>
          </div>
        </div>

        <!-- 卡片体（重叠切换，仅显示当前学段） -->
        <div class="tab-panes">
          <div v-for="lv in levels" :key="'pane-' + lv.key" class="tab-pane"
               v-show="activeLevel === lv.key">

            <!-- 模糊检索框：输入即重置页码 -->
            <div class="search-bar">
              <input type="text" v-model="searchTexts[lv.key]"
                     :placeholder="`搜索${lv.label}图谱…`"
                     @input="resetPage(lv.key)" />
              <span class="clear" v-if="searchTexts[lv.key]" @click="clearSearch(lv.key)">✕</span>
            </div>

            <!-- 图谱卡片列表（当前页） -->
            <div class="kg-list" v-loading="loading">
              <div class="kg-card" v-for="kg in pagedList(lv.key)" :key="kg.kgid">
                <div class="kg-name" :title="kg.name">{{ kg.name }}</div>
                <button class="use-btn" :disabled="kg.using" @click="handleUse(kg, lv.key)">
                  {{ kg.using ? '收藏中…' : '使用知识图谱' }}
                </button>
              </div>
              <div v-if="!loading && !pagedList(lv.key).length" class="empty">
                暂无{{ lv.label }}共享图谱
              </div>
            </div>

            <!-- ★ 分页器：电脑/平板/宽屏手机不变；普通手机三段（上：total+sizes，中：页码，下：prev/next/jumper） -->
            <div class="pagination-bar"
                 v-if="filteredList(lv.key).length > 0"
                 :class="{ phone: isPhone }">
              <el-pagination
                layout="total, sizes, prev, pager, next, jumper"
                :total="filteredList(lv.key).length"
                :page-size="pageSize"
                :page-sizes="pageSizes"
                :current-page="pageNums[lv.key]"
                :pager-count="isPhone ? 5 : 7"
                :small="isPhone"
                @current-change="handlePageChange(lv.key, $event)"
                @size-change="handleSizeChange(lv.key, $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { showSharedKgs, collectGraph } from '@/api/chat/knowledgeGraph';

export default {
  name: 'SharedKnowledge',
  data() {
    return {
      isMobile: window.innerWidth < 1023,
      // ★ 普通手机（窄屏）判定：仅用于分页器三段布局，平板/宽屏手机/电脑均为 false
      isPhone: window.innerWidth < 768,
      loading: false,
      activeLevel: 'h', // 仅作展示初始值，不带入业务 level
      searchTexts: { h: '', m: '', p: '' },
      // { h: [{kgid, name, using}], m: [...], p: [...] }
      sharedMap: { h: [], m: [], p: [] },
      levels: [
        { key: 'h', label: '高中' },
        { key: 'm', label: '初中' },
        { key: 'p', label: '小学' },
      ],
      // ★ 分页
      pageSize: 24,
      pageSizes: [12, 24, 48],
      pageNums: { h: 1, m: 1, p: 1 }, // 每个学段独立页码
    };
  },
  created() {
    this.fetchShared();
    this._onResize = () => {
      this.isMobile = window.innerWidth < 1023;
      this.isPhone = window.innerWidth < 768;
    };
    window.addEventListener('resize', this._onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize);
  },
  methods: {
    handleBack() { this.$emit('close'); },

    async fetchShared() {
      this.loading = true;
      try {
        const { data } = await showSharedKgs();
        // 后端返回 { h:[kgid...], m:[...], p:[...] }
        const toList = (arr) => (arr || []).map(k => {
          const kgid = typeof k === 'string' ? k : (k.kgid || '');
          const name = typeof k === 'object' && k ? (k.name || kgid) : kgid;
          return { kgid, name, using: false };
        });
        this.sharedMap = {
          h: toList(data?.h),
          m: toList(data?.m),
          p: toList(data?.p),
        };
      } catch (e) {
        console.error('[SharedKnowledge] 获取共享图谱失败:', e);
        this.$message.error('获取共享图谱失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },

    // 切换学段（页码独立，切 tab 不重置其它学段）
    switchLevel(key) { this.activeLevel = key; },

    // 模糊检索（不区分大小写），返回该学段筛选后的完整列表
    filteredList(level) {
      const kw = (this.searchTexts[level] || '').trim().toLowerCase();
      const list = this.sharedMap[level] || [];
      if (!kw) return list;
      return list.filter(
        k => k.name.toLowerCase().includes(kw) || k.kgid.toLowerCase().includes(kw)
      );
    },

    // 当前页数据（前端分页）
    pagedList(level) {
      const list = this.filteredList(level);
      const page = this.pageNums[level] || 1;
      const start = (page - 1) * this.pageSize;
      return list.slice(start, start + this.pageSize);
    },

    // 搜索输入时重置该学段页码
    resetPage(level) { this.$set(this.pageNums, level, 1); },
    clearSearch(level) {
      this.searchTexts[level] = '';
      this.resetPage(level);
    },

    // 翻页
    handlePageChange(level, page) {
      this.$set(this.pageNums, level, page);
      // 翻页后滚回卡片体顶部，避免停在上页滚动位置
      this.$nextTick(() => {
        const panes = document.querySelectorAll('.tab-pane');
        panes.forEach(p => { if (p.offsetParent) p.scrollTop = 0; });
      });
    },

    // 每页条数变化 → 该学段页码重置为 1
    handleSizeChange(level, size) {
      this.pageSize = size;
      this.resetPage(level);
    },

    // ★ 使用图谱：收藏 → 通知父组件返回并加载
    async handleUse(kg, level) {
      if (kg.using) return;
      kg.using = true;
      try {
        const res = await collectGraph({ kgid: kg.kgid, level });
        const flag = res && 200 <= res.status && res.status < 300;
        if (!flag) {
          this.$message.error('收藏失败，请稍后重试');
          kg.using = false;
          return;
        }
        this.$message.success('已收藏，正在打开图谱…');
        // 通知 AdvancedLearning：返回并加载该图谱
        this.$emit('use-graph', { kgid: kg.kgid, level, name: kg.name });
      } catch (e) {
        console.error('[SharedKnowledge] 收藏失败:', e);
        this.$message.error('收藏失败：' + (e.message || '未知错误'));
        kg.using = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.shared-modal {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: #fff;
  z-index: 2100;
  display: flex;
  flex-direction: column;
  height: 100vh;
  @supports (height: 100dvh) { height: 100dvh; }

  // ★ 移动端让出顶部标题栏高度（与 AdvancedLearning 保持一致）
  @media (max-width: 1023px) {
    top: 44px;
    height: calc(100vh - 44px);
    @supports (height: 100dvh) { height: calc(100dvh - 44px); }
  }

  .modal-header {
    display: flex;
    align-items: center;
    padding: 12px 20px;
    background: #fff;
    flex-shrink: 0;
    border-bottom: 1px solid #f0f0f0;

    .header-left {
      display: flex;
      align-items: center;
      cursor: pointer;

      .back-icon {
        width: 22px; height: 22px;
        color: rgb(118, 6, 245);
        margin-right: 12px;
        flex-shrink: 0;
        transition: opacity 0.2s;
        &:hover { opacity: 0.6; }
      }

      .modal-title {
        font-size: 18px;
        font-weight: 600;
        color: rgb(118, 6, 245);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .shared-body {
    flex: 1;
    overflow: auto;
    padding: 20px;
    @media (max-width: 1023px) { padding: 12px; }
  }

  .tab-cards { max-width: min(1200px, 100vw); margin: 0 auto; }

  // ★ tab 头（三个标题栏并排，视觉上属于重叠卡片顶部）
  .tab-headers {
    display: flex;
    gap: 4px;

    .tab-header {
      padding: 10px 28px;
      cursor: pointer;
      font-size: 15px;
      font-weight: 600;
      color: #666;
      background: #eef1f8;
      border: 1px solid #dbe4ff;
      border-bottom: none;
      border-radius: 10px 10px 0 0;
      transition: all 0.2s;
      user-select: none;

      .count {
        margin-left: 6px;
        font-size: 12px;
        font-weight: 400;
        color: #999;
      }

      &:hover { color: #525dfc; }

      &.active {
        color: #fff;
        background: linear-gradient(90deg, rgb(82, 93, 252), rgb(222, 96, 253));
        border-color: transparent;
        .count { color: rgba(255, 255, 255, 0.8); }
      }

      @media (max-width: 1023px) {
        flex: 1;
        padding: 10px 8px;
        text-align: center;
        font-size: 14px;
      }
    }
  }

  // ★ 重叠卡片体
  .tab-panes {
    background: #fff;
    border: 1px solid #dbe4ff;
    border-radius: 0 10px 10px 10px;
    box-shadow: 0 4px 16px rgba(46, 91, 255, 0.08);
    min-height: 300px;

    .tab-pane { padding: 16px; }
  }

  .search-bar {
    position: relative;
    margin-bottom: 14px;

    input {
      width: 100%;
      box-sizing: border-box;
      padding: 10px 36px 10px 14px;
      border: 1px solid #dbe4ff;
      border-radius: 22px;
      font-size: 14px;
      outline: none;
      transition: border-color 0.2s;
      &:focus { border-color: #525dfc; }
    }

    .clear {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      cursor: pointer;
      color: #bbb;
      font-size: 13px;
      &:hover { color: #666; }
    }
  }

  .kg-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 12px;
    min-height: 200px;

    @media (max-width: 1023px) {
      grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
      gap: 8px;
    }
  }

  .kg-card {
    padding: 14px;
    background: #f5f7ff;
    border: 1px solid #dbe4ff;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: all 0.2s;

    &:hover {
      background: linear-gradient(90deg, rgb(156, 162, 248), rgb(236, 188, 248));
      box-shadow: 0 4px 12px rgba(143, 46, 255, 0.12);
    }

    .kg-name {
      font-weight: 600;
      font-size: 14px;
      color: #333;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .use-btn {
      padding: 7px 0;
      border: none;
      border-radius: 18px;
      cursor: pointer;
      color: rgb(66, 17, 243);
      font-size: 13px;
      background: rgba(161, 166, 240, 0.08);
      transition: transform 0.2s, opacity 0.2s;

      &:hover:not(:disabled) { transform: translateY(-2px); }
      &:disabled { opacity: 0.6; cursor: not-allowed; }
    }
  }

  .empty {
    color: #999;
    font-size: 14px;
    text-align: center;
    padding: 40px 0;
    grid-column: 1 / -1;
  }

  // ★ 分页器：电脑/平板/宽屏手机保持原横向布局；
  //   普通手机（<768px）三段结构：上（total+sizes）→ 中（页码）→ 下（prev/next/jumper）
  .pagination-bar {
    display: flex;
    justify-content: center;
    padding: 16px 0 4px;
    border-top: 1px solid #f0f0f0;
    margin-top: 14px;

    // ★ 普通手机：三段结构
    &.phone {
      flex-direction: column;
      align-items: center;
      gap: 8px;

      ::v-deep .el-pagination {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        row-gap: 8px;
        white-space: normal;
      }

      // 上段：total + sizes 并排占一行
      ::v-deep .el-pagination__total {
        order: 0;
        margin-right: 8px;
      }
      ::v-deep .el-pagination__sizes {
        order: 0;
        margin-left: 0;

        .el-input .el-input__inner {
          width: 110px; // 收窄，适配小屏
        }
      }

      // 中段：页码居中
      ::v-deep .el-pager {
        order: 1;
        margin: 0 auto;
      }

      // 下段：prev/next/jumper
      ::v-deep .btn-prev,
      ::v-deep .btn-next {
        order: 2;
      }
      ::v-deep .el-pagination__jump {
        order: 2;
        margin-left: 0;
      }
      ::v-deep .el-pagination__editor.el-input {
        width: 40px;
      }
    }
  }
}
</style>
